import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import React from 'react';

import { logout } from 'src/auth';
import AuthErrorPage from 'src/components/Auth/AuthErrorPage';
import Loading from 'src/components/Loading';
import FlashesStore from 'src/stores/FlashesStore';
import { AUTH_ERROR_DEFAULT } from 'src/util/constants';
import { shutdownIntercom } from 'src/util/intercom';
import buildLoginRedirectUrl from 'src/util/links/login';

/**
 * Handles deactivated user error by logging out the user and
 * redirecting to deactivated page
 */
export const handleDeactivatedUserError = () => {
  logout({
    onCompleted: () => {
      console.log('User logged out');
      window.location.replace('/auth/deactivated');
    },
    onError: (err) => {
      console.error('Logout error:', err);
      window.location.replace('/auth/deactivated');
    },
  });
};

/**
 * Handles the application error while login.
 * @param {object} props - props object
 * @param {object} error - error object
 * @returns {React.ReactElement|null} Loading component or null
 */
export const handleApplicationErrors = (props, error) => {
  if (!props && !error) {
    return <Loading />;
  }

  if (error || (props && !props.viewer)) {
    // Clear any existing flash messages
    FlashesStore.reset();

    // Consolidate all error messages from GraphQL errors array
    const errorList = error?.source?.errors;
    if (errorList && Array.isArray(errorList)) {
      const errorMessages = errorList
        .map((err) => err.message)
        .filter((message) => message)
        .join(', ');

      if (errorMessages) {
        // Check whether user is deactivated
        if (errorMessages.includes('lister: user: uuid is nil')) {
          handleDeactivatedUserError();
          return <Loading />;
        }
      }
    }

    // A 401 can leave the browser holding rejected tokens and an identified
    // Intercom session. Clear both local states without a Cognito network call.
    if (error && error.status === 401) {
      const loginUrl = buildLoginRedirectUrl();
      try {
        shutdownIntercom();
      } catch (shutdownIntercomError) {
        console.error('Unable to clear Intercom session:', shutdownIntercomError);
      }
      cognitoUserPoolsTokenProvider.tokenOrchestrator.clearTokens()
        .catch((clearTokensError) => console.error('Unable to clear Amplify tokens:', clearTokensError))
        .finally(() => window.location.replace(loginUrl));
      return <Loading />;
    }

    if (error && error.status === 403) {
      window.location.replace('/403');
      return <Loading />;
    }

    // A successful response with no viewer is the unauthenticated case, with no
    // stale tokens to clear, so redirect straight to login. Any other error
    // (500, unreachable API, etc.) renders the error page without changing the
    // URL, so a server or network failure cannot loop back through login.
    if (!error && props && !props.viewer) {
      window.location.replace(buildLoginRedirectUrl());
      return <Loading />;
    }

    return <AuthErrorPage errorType={AUTH_ERROR_DEFAULT} />;
  }

  return null;
};
